import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import OtpEntry from './auth/OtpEntry';
import Turnstile from './auth/Turnstile';
import { PRIVACY_POLICY_URL, PRIVACY_POLICY_VERSION, isExternalPolicy } from '../utils/privacy';
import { passwordProblem, usernameProblem } from '../utils/passwordRules';

/**
 * Self-registration from the sign-in screen, and the code step that follows
 * it (components/auth/OtpEntry.js; migration 0107).
 *
 * The person says what they ask to be -- an external researcher, a researcher
 * (both say where they work), or lab staff -- and an account made here starts
 * unverified, inactive and without roles: they type the code they are mailed,
 * then a manager approves the request and chooses its roles
 * (routes/account_requests.py). The UI says so up front, so nobody expects to
 * be let straight in. A member of the public registers on /survey instead.
 *
 * Error text comes from the server's error CODE, never its English message --
 * the UI is Hebrew by default. A code this component does not know falls back
 * to a generic line rather than showing a raw key.
 */
const KNOWN_ERRORS = new Set([
  'username_required', 'username_invalid', 'username_reserved', 'email_required',
  'email_invalid', 'password_required', 'password_too_short', 'password_too_long',
  'password_too_weak', 'password_contains_identity', 'password_invalid', 'email_taken',
  'username_taken', 'privacy_not_accepted', 'verification_email_failed', 'rate_limited',
  // The server has no address to put in an emailed link (routes/auth.py).
  'no_app_url',
  // 0106-0107: what is asked for, and the check that a person is asking.
  'captcha_failed', 'invalid_account_type', 'affiliation_required', 'text_too_long',
]);

/** What a sign-up on this screen may ask to be (routes/account_requests.py). */
export const ACCOUNT_TYPES = ['staff', 'external_researcher', 'researcher'];
const NEEDS_AFFILIATION = new Set(['external_researcher', 'researcher']);

export function errorKey(code) {
  return KNOWN_ERRORS.has(code) ? `errors.${code}` : 'errors.generic';
}

/** Whole minutes to wait, from a 429's retry_after (seconds). */
export function waitMinutes(data) {
  return Math.max(1, Math.ceil((Number(data?.retry_after) || 60) / 60));
}

/**
 * Asks for a new verification code (0107), then takes it -- or takes one the
 * person already has ("I have a code"). The server answers identically
 * whether or not the address has an account, so this can say only "if it
 * does, a code is on its way" -- which is the point. `onSignedIn(token)` hears
 * of a public guest the code lets straight in.
 */
export function ResendVerification({ email: knownEmail = '', onSignedIn }) {
  const { t } = useTranslation('register');
  const [email, setEmail] = useState(knownEmail);
  // idle | sending | sent | failed | limited | unconfigured
  const [state, setState] = useState('idle');
  const [wait, setWait] = useState(1);
  const [hasCode, setHasCode] = useState(false);

  const resend = async (e) => {
    e.preventDefault();
    setState('sending');
    try {
      await api.post('/auth/resend-verification', { email: email.trim() });
      setState('sent');
    } catch (err) {
      if (err.response?.status === 429) {
        setWait(waitMinutes(err.response.data));
        setState('limited');
      } else if (err.response?.data?.code === 'no_app_url') {
        // Asked before any account is looked up: it says nothing about the address.
        setState('unconfigured');
      } else {
        setState('failed');
      }
    }
  };

  if (state === 'sent' || hasCode) {
    return <OtpEntry email={email.trim()} justSent={state === 'sent'} onSignedIn={onSignedIn} />;
  }

  return (
    <form onSubmit={resend} data-testid="resend-verification" style={styles.resend}>
      {!knownEmail && (
        <>
          <label htmlFor="resend-email" style={styles.label}>{t('email')}</label>
          <input id="resend-email" type="email" value={email} required dir="ltr"
                 onChange={e => setEmail(e.target.value)} title={t('tip.resendEmail')} style={styles.input} />
        </>
      )}
      <button type="submit" disabled={state === 'sending' || !email.trim()} title={t('tip.resend')} style={styles.secondaryBtn}>
        {state === 'sending' ? t('resending') : t('resend')}
      </button>
      <button type="button" disabled={!email.trim()} onClick={() => setHasCode(true)} title={t('tip.haveCode')}
              style={styles.linkBtn} data-testid="have-code">
        {t('haveCode')}
      </button>
      {state === 'failed' && <p role="alert" style={styles.error}>{t('errors.verification_email_failed')}</p>}
      {state === 'limited' && <p role="alert" style={styles.error}>{t('errors.rate_limited', { minutes: wait })}</p>}
      {state === 'unconfigured' && <p role="alert" style={styles.error}>{t('errors.no_app_url')}</p>}
    </form>
  );
}

/**
 * `afterForm({ accepted, request })`, when given, renders under the form and
 * only while it is shown: the sign-in screen puts "Sign up with Google" there;
 * a ticked privacy box counts as consent for that too, and `request` -- the
 * type, affiliation and purpose filled in -- goes with it.
 */
export default function RegisterPanel({ onBackToSignIn, afterForm, onSignedIn }) {
  const { t } = useTranslation('register');
  const { t: tAccount } = useTranslation('account');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState(null);
  const [approved, setApproved] = useState(false);
  const [accountType, setAccountType] = useState('staff');
  const [affiliation, setAffiliation] = useState('');
  const [purpose, setPurpose] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const request = { account_type: accountType, affiliation: affiliation.trim(), purpose: purpose.trim() };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const name = username.trim();
    const address = email.trim();
    const problem = usernameProblem(name)
      || (address ? null : 'email_required')
      || passwordProblem(password, name, address);
    if (problem) { setError(t(errorKey(problem))); return; }
    if (password !== confirm) { setError(t('errors.passwordMismatch')); return; }
    if (NEEDS_AFFILIATION.has(accountType) && !affiliation.trim()) {
      setError(t('errors.affiliation_required'));
      return;
    }
    // Checked here, and again by the server, which records the version.
    if (!accept) { setError(t('errors.consentRequired')); return; }

    setLoading(true);
    try {
      const res = await api.post('/auth/register', {
        username: name, email: address, password,
        accept_privacy: true, privacy_policy_version: PRIVACY_POLICY_VERSION,
        ...request,
        ...(captchaToken ? { captcha_token: captchaToken } : {}),
      });
      // A developer's machine (start_app.ps1) activates the account at once.
      setApproved(res.data?.auto_approved === true);
      setSentTo(res.data?.sent_to || address);
    } catch (err) {
      const data = err.response?.data;
      setError(t(errorKey(data?.code), { minutes: waitMinutes(data) }));
    } finally {
      setLoading(false);
    }
  };

  if (sentTo && approved) {
    return (
      <div data-testid="register-done" role="status">
        <h2 style={styles.heading}>{t('autoApprovedTitle')}</h2>
        <p style={styles.text}>{t('autoApprovedBody')}</p>
        <button type="button" onClick={onBackToSignIn} title={t('tip.backToSignIn')} style={styles.linkBtn}>
          {t('backToSignIn')}
        </button>
      </div>
    );
  }

  if (sentTo) {
    return (
      <div data-testid="register-done">
        <OtpEntry email={email.trim()} onSignedIn={onSignedIn} onBack={onBackToSignIn} />
      </div>
    );
  }

  return (
    <>
    <form onSubmit={submit} noValidate data-testid="register-form">
      <p style={styles.note}>{t('approvalNote')}</p>

      <fieldset style={styles.types}>
        <legend style={styles.label}>{t('accountType.label')}</legend>
        {ACCOUNT_TYPES.map(type => (
          <label key={type} style={styles.type}>
            <input type="radio" name="account-type" value={type} checked={accountType === type}
                   onChange={() => setAccountType(type)} data-testid={`reg-type-${type}`}
                   title={t(`accountType.${type}Hint`)} />
            <span>
              <strong>{t(`accountType.${type}`)}</strong>
              <span style={styles.hintInline}>{t(`accountType.${type}Hint`)}</span>
            </span>
          </label>
        ))}
        <p style={styles.hint}>
          {t('accountType.publicPrefix')} <Link to="/survey" title={t('tip.publicSurvey')}>{t('accountType.publicLink')}</Link>
        </p>
      </fieldset>

      <label htmlFor="reg-username" style={styles.label}>{t('username')}</label>
      <input id="reg-username" value={username} onChange={e => setUsername(e.target.value)}
             autoComplete="username" dir="ltr" title={t('tip.username')} style={styles.input} />
      <p style={styles.hint}>{t('usernameHint')}</p>

      <label htmlFor="reg-email" style={styles.label}>{t('email')}</label>
      <input id="reg-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
             autoComplete="email" dir="ltr" title={t('tip.email')} style={styles.input} />

      <label htmlFor="reg-password" style={styles.label}>{t('password')}</label>
      <input id="reg-password" type="password" value={password}
             onChange={e => setPassword(e.target.value)} autoComplete="new-password" title={t('tip.password')} style={styles.input} />
      <p style={styles.hint}>{t('passwordHint')}</p>

      <label htmlFor="reg-confirm" style={styles.label}>{t('confirmPassword')}</label>
      <input id="reg-confirm" type="password" value={confirm}
             onChange={e => setConfirm(e.target.value)} autoComplete="new-password" title={t('tip.confirmPassword')} style={styles.input} />

      <label htmlFor="reg-affiliation" style={styles.label}>
        {t(NEEDS_AFFILIATION.has(accountType) ? 'affiliation' : 'affiliationOptional')}
      </label>
      <input id="reg-affiliation" value={affiliation} maxLength={500} onChange={e => setAffiliation(e.target.value)}
             title={t('tip.affiliation')} style={styles.input} data-testid="reg-affiliation" />
      <label htmlFor="reg-purpose" style={styles.label}>{t('purpose')}</label>
      <textarea id="reg-purpose" value={purpose} maxLength={500} rows={2} onChange={e => setPurpose(e.target.value)}
                title={t('tip.purpose')} style={styles.input} data-testid="reg-purpose" />

      <Turnstile onToken={setCaptchaToken} />

      <label style={styles.consent}>
        <input type="checkbox" checked={accept} onChange={e => setAccept(e.target.checked)}
               data-testid="reg-consent" title={t('tip.consent')} />
        <span>
          {tAccount('consentPrefix')}
          <a href={PRIVACY_POLICY_URL} target={isExternalPolicy() ? '_blank' : undefined}
             rel="noopener noreferrer" title={t('tip.privacyPolicy')}>{tAccount('privacyPolicy')}</a>
        </span>
      </label>

      {error && <div role="alert" style={styles.errorBox}>{error}</div>}

      <button type="submit" disabled={loading} title={t('tip.submit')} style={styles.primaryBtn(loading)}>
        {loading ? t('submitting') : t('submit')}
      </button>
    </form>
    {afterForm?.({ accepted: accept, request })}
    </>
  );
}

const styles = {
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--c-text-body-fg)', marginBottom: 4 },
  input: {
    display: 'block', width: '100%', padding: '10px 12px', marginBottom: 6,
    border: '1px solid var(--c-border-strong)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
  },
  hint: { margin: '0 0 12px', fontSize: 12, color: 'var(--c-text-muted-fg)' },
  hintInline: { display: 'block', fontSize: 12, color: 'var(--c-text-muted-fg)' },
  types: { border: 'none', padding: 0, margin: '0 0 12px' },
  type: { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, margin: '4px 0' },
  note: { margin: '0 0 16px', fontSize: 13, color: 'var(--c-text-body-3-fg)', lineHeight: 1.5 },
  text: { margin: '0 0 8px', fontSize: 14, color: 'var(--c-text-2-fg)', lineHeight: 1.5 },
  heading: { margin: '0 0 12px', fontSize: 17, color: 'var(--c-primary-fg)' },
  consent: { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, margin: '8px 0 16px' },
  errorBox: {
    background: 'var(--c-danger-soft)', color: 'var(--c-danger-2-fg)', borderRadius: 8,
    padding: '10px 14px', fontSize: 13, marginBottom: 16,
  },
  primaryBtn: loading => ({
    display: 'block', width: '100%', padding: 11, background: loading ? 'var(--c-primary-3)' : 'var(--c-primary)',
    color: 'var(--c-on-accent)', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
    cursor: loading ? 'not-allowed' : 'pointer',
  }),
  secondaryBtn: {
    padding: '8px 14px', border: '1px solid var(--c-primary)', borderRadius: 8, background: 'var(--c-surface)',
    color: 'var(--c-primary-fg)', fontSize: 13, cursor: 'pointer',
  },
  linkBtn: {
    marginTop: 12, background: 'none', border: 'none', padding: 0, color: 'var(--c-primary-fg)',
    textDecoration: 'underline', cursor: 'pointer', fontSize: 13,
  },
  resend: { display: 'flex', flexDirection: 'column', gap: 8, margin: '12px 0' },
  ok: { margin: 0, fontSize: 13, color: 'var(--c-success-3-fg)' },
  error: { margin: 0, fontSize: 13, color: 'var(--c-danger-2-fg)' },
};
